import { useEffect, useState } from 'react';

const STORAGE_KEY = 'eepass.home.reducedMotion';
const MEDIA_QUERY = '(prefers-reduced-motion: reduce)';

const readStoredPreference = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored === 'true') {
            return true;
        }
        if (stored === 'false') {
            return false;
        }
    } catch (error) {
        // localStorage unavailable (private browsing, disabled, etc.) - fall back to OS setting.
    }

    return null;
};

const readSystemPreference = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false;
    }

    return window.matchMedia(MEDIA_QUERY).matches;
};

/**
 * Returns whether motion (JS-driven fade/position transitions, animations, etc.) should be
 * reduced, combining the OS-level `prefers-reduced-motion` media query with an optional
 * `localStorage`-persisted in-app override (`setReducedMotionPreference`). The stored
 * preference, when set, takes priority over the OS setting so a user can opt in/out of motion
 * regardless of their system configuration; when no stored preference exists, the OS setting is
 * used and kept in sync live if the user changes it while the page is open.
 *
 * Content should always continue to update correctly when reduced motion is active - only the
 * animated transition itself should be skipped.
 */
export const useReducedMotion = () => {
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
        const stored = readStoredPreference();
        return stored !== null ? stored : readSystemPreference();
    });

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }

        const mediaQueryList = window.matchMedia(MEDIA_QUERY);

        const handleChange = (event) => {
            // Only follow the OS setting live if there's no explicit stored override.
            if (readStoredPreference() === null) {
                setPrefersReducedMotion(event.matches);
            }
        };

        if (typeof mediaQueryList.addEventListener === 'function') {
            mediaQueryList.addEventListener('change', handleChange);
            return () => mediaQueryList.removeEventListener('change', handleChange);
        }

        // Safari < 14 fallback.
        mediaQueryList.addListener(handleChange);
        return () => mediaQueryList.removeListener(handleChange);
    }, []);

    const setReducedMotionPreference = (value) => {
        setPrefersReducedMotion(value);
        if (typeof window === 'undefined') {
            return;
        }

        try {
            window.localStorage.setItem(STORAGE_KEY, String(value));
        } catch (error) {
            // Ignore storage failures; the in-memory preference still applies for this session.
        }
    };

    return [prefersReducedMotion, setReducedMotionPreference];
};

export default useReducedMotion;
